import type { AssessmentState, EvidenceGap, Recommendation, TopicResult } from './types'

function baseRec(
  partial: Omit<Recommendation, 'professionalReviewMayBeRequired'> & {
    professionalReviewMayBeRequired?: boolean
  },
): Recommendation {
  return {
    professionalReviewMayBeRequired: false,
    ...partial,
  }
}

export function buildRecommendations(
  state: AssessmentState,
  topics: TopicResult[],
  gaps: EvidenceGap[],
): Recommendation[] {
  const recs: Recommendation[] = []
  const sector = state.companyProfile.sector

  for (const topic of topics.slice(0, 5)) {
    if ((topic.financialScore ?? 0) >= 4 || (topic.impactScore ?? 0) >= 4) {
      recs.push(
        baseRec({
          id: `rec_topic_${topic.topicId}`,
          relatedGapOrTopic: topic.topicId,
          priority: (topic.financialScore ?? 0) >= 5 || (topic.impactScore ?? 0) >= 5 ? 'critical' : 'high',
          action: `Validate materiality for “${topic.name}” with internal owners and, where needed, stakeholder input. Document why the topic is prioritized and identify evidence already available.`,
          owner: 'Sustainability or ESG lead',
          timeframe: '0-30',
          informationRequired: topic.missingEvidence.join('; ') || 'Topic evidence and owner mapping',
          expectedDeliverable: `One-page topic brief for ${topic.name} with owners, evidence list and next decision`,
          sourceOrMethod: 'Double-materiality style self-assessment (preliminary)',
          whyRelevant: `You rated this topic among your higher priorities (financial: ${topic.financialLabel}, impact: ${topic.impactLabel}).`,
          professionalReviewMayBeRequired: true,
        }),
      )
    }
  }

  for (const gap of gaps) {
    if (gap.severity === 'high') {
      recs.push(
        baseRec({
          id: `rec_gap_${gap.id}`,
          relatedGapOrTopic: gap.id,
          priority: 'high',
          action: gap.description,
          owner: sector === 'financial_services' ? 'Risk / sustainability lead' : 'Operations or sustainability manager',
          timeframe: '0-30',
          informationRequired: 'Source records, facility coverage and responsible department',
          expectedDeliverable: 'Documented gap-closure plan with owners and due dates',
          sourceOrMethod: 'Inventory readiness review',
          whyRelevant: `Identified as a ${gap.severity}-severity evidence or process gap in ${gap.area}.`,
        }),
      )
    }
  }

  const electricity = state.carbon.s2_electricity
  if (electricity?.exists === 'yes' && electricity.dataCollected === 'yes' && electricity.coverage !== 'all') {
    recs.push(
      baseRec({
        id: 'rec_electricity_standardize',
        relatedGapOrTopic: 's2_electricity',
        priority: 'high',
        action:
          'Establish a standardized monthly electricity-data collection process for all in-scope facilities. Assign a data owner, document source records, define the reporting boundary and introduce a review step.',
        owner: 'Facilities or sustainability manager',
        timeframe: '0-30',
        informationRequired: 'Utility accounts, meter list, facility inventory',
        expectedDeliverable: 'Monthly electricity register with facility coverage and source references',
        sourceOrMethod: 'GHG Protocol Scope 2 readiness practice',
        whyRelevant: 'Electricity data appears partially available but coverage across facilities is incomplete.',
      }),
    )
  }

  if (electricity?.exists === 'yes' && electricity.dataCollected === 'no') {
    recs.push(
      baseRec({
        id: 'rec_electricity_collect',
        relatedGapOrTopic: 's2_electricity',
        priority: 'critical',
        action:
          'Identify electricity accounts and responsible departments. Begin collecting bills or meter readings for the intended reporting period. Missing data is a readiness gap — not zero emissions.',
        owner: 'Facilities manager',
        timeframe: '0-30',
        informationRequired: 'Utility account list and billing contacts',
        expectedDeliverable: 'Electricity data request tracker for in-scope sites',
        sourceOrMethod: 'GHG Protocol Scope 2',
        whyRelevant: 'Purchased electricity is present but activity data is not yet collected.',
      }),
    )
  }

  const fuel = state.carbon.s1_mobile
  if (fuel?.exists === 'yes' && (fuel.dataCollected === 'no' || fuel.dataCollected === 'not_sure')) {
    recs.push(
      baseRec({
        id: 'rec_fuel_records',
        relatedGapOrTopic: 's1_mobile',
        priority: 'high',
        action: 'Identify fuel and fleet records and assign a responsible department for mobile combustion data.',
        owner: 'Fleet or operations manager',
        timeframe: '0-30',
        informationRequired: 'Fuel cards, telematics, purchase invoices',
        expectedDeliverable: 'Fleet fuel data map with owners',
        sourceOrMethod: 'GHG Protocol Scope 1',
        whyRelevant: 'Owned/controlled vehicles appear relevant but fuel activity data is missing or uncertain.',
      }),
    )
  }

  const refrigerants = state.carbon.s1_refrigerants
  if (refrigerants?.exists === 'yes' && refrigerants.dataCollected !== 'yes') {
    recs.push(
      baseRec({
        id: 'rec_refrigerant_inventory',
        relatedGapOrTopic: 's1_refrigerants',
        priority: 'high',
        action:
          'Inventory relevant cooling equipment and establish leak / servicing data collection procedures for refrigerant tracking.',
        owner: 'Facilities manager',
        timeframe: '30-60',
        informationRequired: 'Equipment register, refrigerant types and charges',
        expectedDeliverable: 'Refrigerant equipment inventory with service log template',
        sourceOrMethod: 'GHG Protocol fugitive emissions',
        whyRelevant: 'Refrigerant sources exist but activity data collection is not established.',
      }),
    )
  }

  const scope3Screened = Object.entries(state.carbon).filter(
    ([id, r]) => id.startsWith('s3_') && r.exists === 'yes',
  )
  const scope3Unscreened = scope3Screened.filter(
    ([, r]) => r.dataCollected === 'no' || r.methodDocumented === 'no' || !r.readiness || r.readiness === 'not_implemented',
  )
  if (scope3Unscreened.length >= 2) {
    recs.push(
      baseRec({
        id: 'rec_scope3_screen',
        relatedGapOrTopic: 'scope3',
        priority: 'high',
        action:
          'Screen potentially relevant Scope 3 categories for your sector. Document inclusions, exclusions and reasons. Do not treat missing data as proof a category is immaterial.',
        owner: 'Sustainability lead',
        timeframe: '30-60',
        informationRequired: 'Spend data, logistics records, travel and waste reports',
        expectedDeliverable: 'Scope 3 screening memo with inclusion/exclusion log',
        sourceOrMethod: 'GHG Protocol Corporate Value Chain (Scope 3) Standard',
        whyRelevant: 'Several Scope 3 categories appear relevant but are not yet screened or method-documented.',
        professionalReviewMayBeRequired: true,
      }),
    )
  }

  if (state.responses.ev_emission_factors === 'not_implemented' || state.responses.ev_emission_factors === 'partial') {
    recs.push(
      baseRec({
        id: 'rec_ef_register',
        relatedGapOrTopic: 'ev_emission_factors',
        priority: 'medium',
        action: 'Create an emission-factor source and version register for all calculation methods in use.',
        owner: 'Sustainability analyst',
        timeframe: '30-60',
        informationRequired: 'Factor sources, publication years, units',
        expectedDeliverable: 'Version-controlled emission-factor register',
        sourceOrMethod: 'Inventory documentation good practice',
        whyRelevant: 'Emission-factor traceability is only partially implemented or not implemented.',
      }),
    )
  }

  if (state.responses.ev_boundary === 'not_implemented' || state.responses.ev_boundary === 'not_sure') {
    recs.push(
      baseRec({
        id: 'rec_boundary',
        relatedGapOrTopic: 'ev_boundary',
        priority: 'critical',
        action:
          'Document organizational and operational boundaries for GHG and sustainability data before expanding calculations.',
        owner: 'Sustainability lead with finance input',
        timeframe: '0-30',
        informationRequired: 'Entity list, operational control vs equity share approach, facility list',
        expectedDeliverable: 'Boundary memo approved by a named owner',
        sourceOrMethod: 'GHG Protocol corporate standard',
        whyRelevant: 'Reporting boundaries are not yet defined or are uncertain.',
        professionalReviewMayBeRequired: true,
      }),
    )
  }

  if (state.responses.ev_review === 'not_implemented' || state.responses.ev_review === 'partial') {
    recs.push(
      baseRec({
        id: 'rec_review_process',
        relatedGapOrTopic: 'ev_review',
        priority: 'medium',
        action: 'Define review and approval responsibilities for ESG/GHG data before external responses.',
        owner: 'Sustainability lead',
        timeframe: '30-60',
        informationRequired: 'RACI for data owners and approvers',
        expectedDeliverable: 'Documented review checklist and approval log',
        sourceOrMethod: 'Reporting controls practice',
        whyRelevant: 'A formal review step before reporting is incomplete.',
      }),
    )
  }

  if (state.responses.rc_designated_mrv === 'yes' || state.responses.rc_designated_mrv === 'not_sure') {
    recs.push(
      baseRec({
        id: 'rec_mrv_designation_review',
        relatedGapOrTopic: 'rc_designated_mrv',
        priority: 'critical',
        action:
          'Confirm with legal/compliance owners whether any UAE ministry or competent authority has designated or notified the company for emissions measurement, inventory or reporting. Document the notice, authority and scope. Do not rely on this self-assessment as confirmation.',
        owner: 'Compliance / sustainability lead',
        timeframe: '0-30',
        informationRequired: 'Authority correspondence, designation notice, free-zone vs mainland competent authority map',
        expectedDeliverable: 'Designation status memo with next actions',
        sourceOrMethod: 'Federal Decree-Law No. 11 of 2024 — verification of determination/designation',
        whyRelevant:
          state.responses.rc_designated_mrv === 'yes'
            ? 'You reported designation/notification for emissions measurement or reporting.'
            : 'Designation status is uncertain and should be clarified before concluding MRV duties.',
        professionalReviewMayBeRequired: true,
      }),
    )
  }

  if (state.responses.sec_con_embodied === 'no' || state.responses.sec_con_embodied === 'not_sure') {
    recs.push(
      baseRec({
        id: 'rec_embodied_carbon',
        relatedGapOrTopic: 'sec_con_embodied',
        priority: 'high',
        action:
          'Identify major construction materials and request embodied-carbon / EPD data from suppliers for priority projects.',
        owner: 'Procurement / project sustainability lead',
        timeframe: '30-60',
        informationRequired: 'Material quantities, supplier contacts, available EPDs',
        expectedDeliverable: 'Priority-materials embodied-carbon data request log',
        sourceOrMethod: 'Sector Scope 3 screening for construction materials',
        whyRelevant: 'Construction-sector answers indicate embodied-carbon data is missing or uncertain.',
      }),
    )
  }

  if (state.responses.sec_re_tenant_data === 'no' || state.responses.sec_re_portfolio_coverage === 'partial') {
    recs.push(
      baseRec({
        id: 'rec_re_boundary',
        relatedGapOrTopic: 'sec_re_tenant_data',
        priority: 'high',
        action:
          'Map landlord versus tenant meters and clarify portfolio coverage for energy data before expanding GHG calculations.',
        owner: 'Property / facilities manager',
        timeframe: '0-30',
        informationRequired: 'Building list, meter ownership, lease clauses on data access',
        expectedDeliverable: 'Building energy boundary and coverage register',
        sourceOrMethod: 'GHG Protocol boundary practice for leased assets',
        whyRelevant: 'Real-estate answers show tenant/landlord or portfolio coverage gaps.',
      }),
    )
  }

  if (state.responses.sec_fin_financed === 'no' || state.responses.sec_fin_financed === 'not_sure') {
    recs.push(
      baseRec({
        id: 'rec_financed_screen',
        relatedGapOrTopic: 'sec_fin_financed',
        priority: 'medium',
        action:
          'Screen whether financed-emissions or investment-portfolio climate data may be relevant. Document inclusion/exclusion rationale — do not skip screening because data is hard to obtain.',
        owner: 'Risk / sustainability lead',
        timeframe: '30-60',
        informationRequired: 'Portfolio categories, data availability assessment',
        expectedDeliverable: 'Financed-emissions screening memo',
        sourceOrMethod: 'PCAF / GHG Protocol related practice (methodology choice to be documented)',
        whyRelevant: 'Financial-services answers indicate financed-emissions relevance has not been screened.',
        professionalReviewMayBeRequired: true,
      }),
    )
  }

  const canRespond = state.responses.st_can_respond
  if (canRespond === 'no' || canRespond === 'partial') {
    recs.push(
      baseRec({
        id: 'rec_customer_response',
        relatedGapOrTopic: 'st_can_respond',
        priority: 'high',
        action:
          'Map active customer or lender disclosure requests to available company data. Create a response pack for the most frequent questionnaires.',
        owner: 'Sustainability or commercial lead',
        timeframe: '0-30',
        informationRequired: 'Recent questionnaires and available metrics',
        expectedDeliverable: 'Request-to-data mapping and first response pack',
        sourceOrMethod: 'Stakeholder response readiness',
        whyRelevant: 'The company cannot yet respond reliably to ESG information requests.',
      }),
    )
  }

  // Deduplicate by id, keep highest priority order
  const priorityRank = { critical: 0, high: 1, medium: 2, low: 3 }
  const unique = new Map<string, Recommendation>()
  for (const rec of recs) unique.set(rec.id, rec)
  return [...unique.values()]
    .sort((a, b) => priorityRank[a.priority] - priorityRank[b.priority] || a.id.localeCompare(b.id))
    .slice(0, 12)
}
